import { z } from "zod";
import {
  MAX_INSTALLMENTS,
  MAX_PRICE_CENTS,
  MAX_PRICE_RATE_PCT4,
  MAX_STOCK_QTY_Q4,
  MAX_TERM_DAYS,
  MIN_PRICE_RATE_PCT4,
  PRICE_LIST_BASES,
  PRICE_LIST_KINDS,
  isIsoDate,
  termProblem,
  type PriceReason,
  type PriceSource,
} from "@cx/domain";
import { listQuery } from "./common";
import { blank } from "./fields";
import { isoDateValue } from "./contacts";
import { ulidParam } from "./ids";

// ===== Listas de preço (RF-CAD-04) =====

/**
 * Datas de preço e parcelas entre 1900 e 2199: perto de 9999 o vencimento passaria de 4 dígitos e antes de 1583 não
 * há Páscoa gregoriana (feriados). A comparação de texto vale para AAAA-MM-DD.
 */
const MIN_DATE = "1900-01-01";
const MAX_DATE = "2199-12-31";
export const businessDate = isoDateValue.refine(
  // Data que já falhou no formato não ganha um segundo erro.
  (d) => !isIsoDate(d) || (d >= MIN_DATE && d <= MAX_DATE),
  "Use uma data entre 01/01/1900 e 31/12/2199",
);
const rate = z
  .number("Informe o percentual")
  .int("Percentual inválido")
  .min(MIN_PRICE_RATE_PCT4, "O desconto vai até 100%")
  .max(MAX_PRICE_RATE_PCT4, "O acréscimo vai até 1000%");
const optDate = blank.pipe(businessDate.nullable());
const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);

export const priceListInput = z
  .object({
    name: z.string().trim().min(1, "Informe o nome").max(60, "Até 60 caracteres"),
    kind: z.enum(PRICE_LIST_KINDS, "Tipo inválido"),
    /** + acréscimo / − desconto (pct4). Taxa e Por período; a Customizada não tem % geral. */
    ratePct4: nullish(rate),
    base: z.enum(PRICE_LIST_BASES, "Base inválida").default("price"),
    validFrom: optDate,
    validTo: optDate,
    active: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (v.kind !== "custom" && v.ratePct4 === null)
      ctx.addIssue({ code: "custom", path: ["ratePct4"], message: "Informe o percentual" });
    if (v.kind === "custom" && v.ratePct4 !== null)
      ctx.addIssue({
        code: "custom",
        path: ["ratePct4"],
        message: "Na lista customizada o preço ou o percentual vão em cada produto",
      });
    if (v.kind === "period") {
      if (!v.validFrom) ctx.addIssue({ code: "custom", path: ["validFrom"], message: "Informe o início" });
      if (!v.validTo) ctx.addIssue({ code: "custom", path: ["validTo"], message: "Informe o fim" });
    }
    // O refinamento roda mesmo com a data inválida (Zod 4): só compara datas válidas.
    if (v.validFrom && v.validTo && isIsoDate(v.validFrom) && isIsoDate(v.validTo) && v.validTo < v.validFrom)
      ctx.addIssue({
        code: "custom",
        path: ["validTo"],
        message: "O fim deve ser igual ou posterior ao início",
      });
  });
export type PriceListInput = z.output<typeof priceListInput>;
export type PriceListFormValues = z.input<typeof priceListInput>;
/** `ratePct4` ausente na lista de base "custo" para quem não tem products:read_cost. */
export type PriceListView = Omit<PriceListInput, "ratePct4"> & {
  ratePct4?: number | null;
  id: string;
  itemCount: number;
};

/** Item da lista customizada: preço fixo ou % sobre a base da lista, com quantidade mínima opcional. */
export const priceListItemInput = z
  .object({
    priceCents: nullish(
      z
        .number("Informe o preço")
        .int("Use centavos inteiros")
        .min(0, "Preço não pode ser negativo")
        .max(MAX_PRICE_CENTS, "Preço acima do limite"),
    ),
    ratePct4: nullish(rate),
    minQtyQ4: nullish(
      z
        .number()
        .int("Quantidade inválida")
        .min(0, "Quantidade não pode ser negativa")
        .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
    ),
  })
  .superRefine((v, ctx) => {
    if (v.priceCents === null && v.ratePct4 === null)
      ctx.addIssue({ code: "custom", path: ["priceCents"], message: "Informe o preço ou o percentual" });
    if (v.priceCents !== null && v.ratePct4 !== null)
      ctx.addIssue({
        code: "custom",
        path: ["ratePct4"],
        message: "Informe o preço ou o percentual, não os dois",
      });
  });
export type PriceListItemInput = z.output<typeof priceListItemInput>;

export const priceListItemsQuery = listQuery;
export type PriceListItemsQuery = z.output<typeof priceListItemsQuery>;

/** Item com os dados do produto (sem custo). `ratePct4` ausente na lista de custo sem products:read_cost. */
export type PriceListItemView = Omit<PriceListItemInput, "ratePct4"> & {
  ratePct4?: number | null;
  productId: string;
  productName: string;
  sku: string | null;
  unit: string;
  productPriceCents: number;
};

export const priceListProductParam = z.object({ id: ulidParam, productId: ulidParam });

export const priceResolveQuery = z.object({
  productId: ulidParam,
  qtyQ4: z.coerce
    .number()
    .int("Quantidade inválida")
    .min(0, "Quantidade não pode ser negativa")
    .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite")
    .default(10_000),
  /** Ausente = hoje em America/Sao_Paulo. */
  date: businessDate.optional(),
});
export type PriceResolveQuery = z.output<typeof priceResolveQuery>;

/** Preço efetivo (05 §2). Nunca traz o custo. */
export type PriceResolveResult = {
  productId: string;
  priceListId: string | null;
  priceCents: number;
  /** Preço de venda do cadastro. */
  basePriceCents: number;
  source: PriceSource;
  reason: PriceReason | null;
  date: string;
};

// ===== Condições de pagamento =====

const DAYS_MSG = `Cada prazo deve ser um número inteiro de 0 a ${MAX_TERM_DAYS} dias`;

export const paymentTermInput = z
  .object({
    name: z.string().trim().min(1, "Informe o nome").max(60, "Até 60 caracteres"),
    /** Prazos em dias a partir da data base, crescentes ("30/60/90"). */
    days: z
      .array(
        z.number(DAYS_MSG).int(DAYS_MSG).min(0, DAYS_MSG).max(MAX_TERM_DAYS, DAYS_MSG),
        "Informe os prazos",
      )
      .max(MAX_INSTALLMENTS, `No máximo ${MAX_INSTALLMENTS} parcelas`),
    /** Entrada à vista: uma parcela a mais, vencendo na data base. */
    downPayment: z.boolean().default(false),
    active: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    // Acima do teto o .max() já reclamou (o Zod 4 roda o refinamento mesmo assim).
    if (v.days.length > MAX_INSTALLMENTS) return;
    const problem = termProblem(v);
    if (problem) ctx.addIssue({ code: "custom", path: ["days"], message: problem });
  });
export type PaymentTermInput = z.output<typeof paymentTermInput>;
export type PaymentTermView = PaymentTermInput & { id: string };

export const paymentTermsSettingsInput = z.object({ postponeToBusinessDay: z.boolean() });
export type PaymentTermsSettings = z.infer<typeof paymentTermsSettingsInput>;

export const installmentsQuery = z.object({
  totalCents: z.coerce
    .number()
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(MAX_PRICE_CENTS, "Valor acima do limite"),
  /** Ausente = hoje em America/Sao_Paulo. */
  baseDate: businessDate.optional(),
});
export type InstallmentsQuery = z.output<typeof installmentsQuery>;

export type InstallmentView = { number: number; dueDate: string; amountCents: number };
